import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Mail, X, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../config/backendUrl';
/**
 * Banner fixo que aparece no topo quando o usuário não confirmou o e-mail.
 * É escondido para usuários:
 *  - não autenticados
 *  - já verificados (`email_verified === true`)
 *  - admin (`plan === 'master'` ou `is_master`)
 *  - quando o usuário fecha manualmente o banner na sessão (dismissed)
 */
const EmailVerificationBanner = () => {
  const { user, isAuthenticated } = useAuth();
  const [dismissed, setDismissed] = useState(
    typeof window !== 'undefined' && sessionStorage.getItem('emailVerifyBannerDismissed') === '1'
  );
  const [sending, setSending] = useState(false);
  const [sentMessage, setSentMessage] = useState(null);

  // Não exibir em rotas de login/register para não poluir
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const isAuthRoute = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email'].some(
    (p) => pathname.startsWith(p)
  );

  useEffect(() => {
    if (user?.email_verified) {
      setDismissed(false);
      sessionStorage.removeItem('emailVerifyBannerDismissed');
    }
  }, [user?.email_verified]);

  if (!isAuthenticated || !user) return null;
  if (user.email_verified) return null;
  if (user.plan === 'master' || user.is_master) return null;
  if (isAuthRoute) return null;
  if (dismissed) return null;

  const handleResend = async () => {
    setSending(true);
    setSentMessage(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/auth/send-verification-email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSentMessage(res.data.message || 'Email enviado!');
      toast.success('📧 Email de confirmação enviado!');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail?.code === 'RESEND_COOLDOWN') {
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Erro ao enviar email. Tente novamente.');
      }
    } finally {
      setSending(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('emailVerifyBannerDismissed', '1');
  };

  return (
    <div
      className="bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md"
      data-testid="email-verification-banner"
      role="alert"
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
        <Mail className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          {sentMessage ? (
            <span className="flex items-center gap-2 text-sm font-medium">
              <CheckCircle className="w-4 h-4" />
              {sentMessage}
            </span>
          ) : (
            <span className="text-sm font-medium">
              <strong>🎁 Confirme seu e-mail e ganhe +1 obra grátis!</strong>{' '}
              Verifique sua caixa para liberar o registro das suas obras.
              {user.email ? <> Link enviado para <strong>{user.email}</strong>.</> : null}
            </span>
          )}
        </div>
        {!sentMessage && (
          <button
            type="button"
            onClick={handleResend}
            disabled={sending}
            className="px-4 py-1.5 bg-white text-orange-600 rounded-lg font-semibold text-sm hover:bg-orange-50 disabled:opacity-60 transition-colors flex items-center gap-2"
            data-testid="email-verification-resend-btn"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Enviando...
              </>
            ) : (
              'Reenviar email'
            )}
          </button>
        )}
        <button
          type="button"
          onClick={handleDismiss}
          className="text-white hover:text-orange-100 p-1"
          aria-label="Fechar aviso"
          data-testid="email-verification-dismiss-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
